
import React, {
  useState,
  useEffect,
} from "react"

import { useNavigate } from "react-router"
import useTitle from "@/hooks/useTitle"
import PulseLoader from "react-spinners/PulseLoader"

import {
  useCreateNewUserSchedulingPageMutation,
} from "@/features/userSchedulingPages/userSchedulingPagesApiSlice"

import {
  useGetOrgIntentionBoardsQuery,
} from "@/features/orgIntentionBoards/orgIntentionBoardsApiSlice"


const initialFormState = {
  intentionBoardId: "",
  communicationType: "",
  pageName: "",
  publicSlug: "",
  passcode: "",
  pageDescription: "",
  googleCalendarId: "primary",

  allowedTimezones: [
    { label: "US Pacific", timezone: "America/Los_Angeles" },
    { label: "US Mountain", timezone: "America/Denver" },
    { label: "US Central", timezone: "America/Chicago" },
    { label: "US Eastern", timezone: "America/New_York" },
    { label: "UK", timezone: "Europe/London" },
    { label: "India", timezone: "Asia/Kolkata" },
  ],
}


const EtCreateUserSchedulingPage = ({
  setActiveAdminView,
}) => {

  // First Created - 06 May 2026
  // Author - Atul Pandey
  // Purpose - To create a Trinity SlotBoard scheduling page.
  // Updated - 18 May 2026 - Added US Mountain timezone
  // Updated - 27 Sep 2026 - Added Intention Board association
  // Updated - 27 Sep 2026 - Added optional communication type

  useTitle("Create SlotBoard Page")

  const navigate = useNavigate()

  const [formData, setFormData] =
    useState(initialFormState)

  const [
    createNewUserSchedulingPage,
    {
      isLoading,
      isSuccess,
      isError,
      error,
    },
  ] = useCreateNewUserSchedulingPageMutation()


  /*
  |--------------------------------------------------------------------------
  | Intention Boards
  |--------------------------------------------------------------------------
  */

  const {
    data: intentionBoardsData,
    isLoading: isLoadingIntentionBoards,
    isError: isIntentionBoardsError,
    error: intentionBoardsError,
  } = useGetOrgIntentionBoardsQuery({})

  const intentionBoards =
    intentionBoardsData?.ids
      ?.map(
        id =>
          intentionBoardsData?.entities?.[id]
      )
      ?.filter(Boolean)
      ?.filter(
        board =>
          board?.isActive !== false
      ) || []


  /*
  |--------------------------------------------------------------------------
  | Successful Creation
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    if (!isSuccess) return

    setFormData(initialFormState)

    if (setActiveAdminView) {
      setActiveAdminView("userSchedulingPages")
    } else {
      navigate("/userSchedulingPages")
    }

  }, [
    isSuccess,
    navigate,
    setActiveAdminView,
  ])


  /*
  |--------------------------------------------------------------------------
  | Form Helpers
  |--------------------------------------------------------------------------
  */

  const handleChange = e => {

    const { name, value } = e.target

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))

  }


  const handleTimezoneChange = (
    index,
    key,
    value
  ) => {

    setFormData(prev => ({

      ...prev,

      allowedTimezones:
        prev.allowedTimezones.map(
          (item, i) =>
            i === index
              ? {
                  ...item,
                  [key]: value,
                }
              : item
        ),

    }))

  }


  const handleSubmit = async e => {

    e?.preventDefault()

    if (
      !formData.intentionBoardId
      ||
      !formData.pageName.trim()
      ||
      !formData.publicSlug.trim()
      ||
      !formData.passcode.trim()
    ) {
      return
    }

    const pageData = {

      intentionBoardId:
        formData.intentionBoardId,

      communicationType:
        formData.communicationType || null,

      pageName:
        formData.pageName.trim(),

      publicSlug:
        formData.publicSlug.trim().toLowerCase(),

      passcode:
        formData.passcode,

      pageDescription:
        formData.pageDescription,

      googleCalendarId:
        formData.googleCalendarId,

      allowedTimezones:
        formData.allowedTimezones,

    }

    try {

      await createNewUserSchedulingPage(
        pageData
      ).unwrap()

    } catch (error) {

      console.error(
        "SlotBoard creation error:",
        error
      )

    }

  }


  const handleCancel = () => {

    if (setActiveAdminView) {
      setActiveAdminView("userSchedulingPages")
    } else {
      navigate("/userSchedulingPages")
    }

  }


  const handleClear = () => {
    setFormData(initialFormState)
  }


  const canCreate =
    Boolean(formData.intentionBoardId)
    &&
    Boolean(formData.pageName.trim())
    &&
    Boolean(formData.publicSlug.trim())
    &&
    Boolean(formData.passcode.trim())
    &&
    !isLoading


  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (

    <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black font-poppins">

      {isLoading && (
        <PulseLoader color="#ea580c" size={5} />
      )}

      {isError && (
        <p className="text-orange-700 text-xs font-bold p-2">
          {error?.data?.message || "Unable to create SlotBoard."}
        </p>
      )}

      <div className="bg-white p-3 m-1 rounded">

        <p className="font-bold text-xl text-gray-800">
          Add a SlotBoard Page
        </p>

        <p className="text-[10px] text-gray-500 mt-1">
          Connect this scheduling page to an Intention Board.
        </p>

        <form
          onSubmit={handleSubmit}
          className="mt-3 text-xs space-y-3"
        >

          {/* Intention Board */}

          <div className="bg-orange-50 border border-orange-100 rounded p-3">

            <label className="block text-[10px] font-bold text-orange-700 mb-1">
              Intention Board *
            </label>

            <select
              name="intentionBoardId"
              value={formData.intentionBoardId}
              onChange={handleChange}
              disabled={isLoadingIntentionBoards}
              className="w-full rounded border border-orange-200 bg-white p-2 text-xs"
              required
            >

              <option value="">
                Select an Intention Board
              </option>

              {intentionBoards.map(board => (

                <option
                  key={board._id}
                  value={board._id}
                >
                  {board.boardName || "Intention Board"}
                </option>

              ))}

            </select>

            {isLoadingIntentionBoards && (
              <p className="text-[9px] text-gray-500 mt-2">
                Loading Intention Boards...
              </p>
            )}

            {isIntentionBoardsError && (
              <p className="text-[10px] text-red-700 mt-2">
                {intentionBoardsError?.data?.message ||
                  "Unable to load Intention Boards."}
              </p>
            )}

            {!isLoadingIntentionBoards &&
              !isIntentionBoardsError &&
              intentionBoards.length === 0 && (

                <p className="text-[9px] text-orange-700 mt-2">
                  No available Intention Boards were found.
                </p>

              )}

            <p className="text-[9px] text-gray-500 mt-2">
              Every new SlotBoard must belong to one Intention Board.
            </p>

          </div>


          {/* Communication Type */}

          <div className="bg-gray-50 rounded p-3">

            <label className="block text-[10px] font-bold text-gray-700 mb-1">
              Communication Type (Optional)
            </label>

            <select
              name="communicationType"
              value={formData.communicationType}
              onChange={handleChange}
              className="w-full rounded border border-gray-200 bg-white p-2 text-xs"
            >

              <option value="">
                Uncategorized
              </option>

              <option value="initial_1on1">
                Initial 1:1
              </option>

              <option value="deep_dive">
                Deep Dive
              </option>

            </select>

            <p className="text-[9px] text-gray-500 mt-2">
              Categorization helps the Intention Board recognize the correct booking milestone.
            </p>

          </div>


          {/* Page Name */}

          <div>
            <label className="block text-[10px] font-bold text-gray-600 mb-1">
              SlotBoard Page Name *
            </label>

            <input
              type="text"
              name="pageName"
              placeholder="Enter the SlotBoard page name"
              value={formData.pageName}
              onChange={handleChange}
              className="w-full rounded border border-gray-200 p-2"
              required
            />
          </div>


          {/* Public Slug */}

          <div>

            <label className="block text-[10px] font-bold text-gray-600 mb-1">
              Public Slug *
            </label>

            <input
              type="text"
              name="publicSlug"
              placeholder="For example catalyst-cohort"
              value={formData.publicSlug}
              onChange={handleChange}
              className="w-full rounded border border-gray-200 p-2"
              required
            />

            <p className="text-[9px] text-gray-500 mt-1">
              This is used in the public booking URL.
            </p>

          </div>


          {/* Passcode */}

          <div>

            <label className="block text-[10px] font-bold text-gray-600 mb-1">
              Access Passcode *
            </label>

            <input
              type="text"
              name="passcode"
              placeholder="Enter access passcode"
              value={formData.passcode}
              onChange={handleChange}
              className="w-full rounded border border-gray-200 p-2"
              required
            />

          </div>


          {/* Description */}

          <div>

            <label className="block text-[10px] font-bold text-gray-600 mb-1">
              Description
            </label>

            <textarea
              name="pageDescription"
              placeholder="Add a short description for cohort users"
              value={formData.pageDescription}
              onChange={handleChange}
              className="w-full rounded border border-gray-200 p-2"
              rows={4}
            />

          </div>


          {/* Google Calendar ID */}

          <div>

            <label className="block text-[10px] font-bold text-gray-600 mb-1">
              Google Calendar ID
            </label>

            <input
              type="text"
              name="googleCalendarId"
              placeholder="Google Calendar ID"
              value={formData.googleCalendarId}
              onChange={handleChange}
              className="w-full rounded border border-gray-200 p-2"
            />

            <p className="text-[9px] text-gray-500 mt-1">
              Keep as primary unless using a specific Google Calendar.
            </p>

          </div>


          {/* Timezones */}

          <div className="bg-gray-50 rounded p-3">

            <p className="text-[10px] font-bold text-gray-700">
              Displayed Timezones
            </p>

            <p className="text-[9px] text-gray-500 mt-1 mb-3">
              These timezone labels are shown to public users.
            </p>

            {formData.allowedTimezones.map(
              (item, index) => (

                <div
                  key={index}
                  className="grid grid-cols-12 gap-2 items-center mb-2"
                >

                  <input
                    type="text"
                    value={item.label}
                    onChange={e =>
                      handleTimezoneChange(
                        index,
                        "label",
                        e.target.value
                      )
                    }
                    placeholder="Timezone label"
                    className="col-span-4 min-w-0 rounded border border-gray-200 p-2 text-xs"
                  />

                  <input
                    type="text"
                    value={item.timezone}
                    onChange={e =>
                      handleTimezoneChange(
                        index,
                        "timezone",
                        e.target.value
                      )
                    }
                    placeholder="IANA timezone"
                    className="col-span-8 min-w-0 rounded border border-gray-200 p-2 text-xs"
                  />

                </div>

              )
            )}

          </div>


          {/* Actions */}

          <div className="flex flex-wrap justify-end gap-2 bg-gray-50 rounded p-3">

            <button
              type="button"
              onClick={handleCancel}
              className="bg-gray-200 text-gray-700 rounded px-4 py-2 text-xs font-bold"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleClear}
              className="bg-white border border-gray-200 text-gray-700 rounded px-4 py-2 text-xs font-bold"
            >
              Clear
            </button>

            <button
              type="submit"
              disabled={!canCreate}
              className="bg-orange-600 hover:bg-orange-700 text-white rounded px-4 py-2 text-xs font-bold disabled:opacity-40"
            >
              {isLoading ? "Creating..." : "Create"}
            </button>

          </div>

        </form>

      </div>

    </div>

  )

}

export default EtCreateUserSchedulingPage